// src/Components/FloorSelector.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import FloorAddModal from './FloorAddModal';

function getRoleFromToken() {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.role || null;
  } catch {
    return null;
  }
}

// 이름만 보고 정렬 순서 계산 (level은 사용 안 함)
function floorOrder(name = '') {
  const s = String(name).trim().toUpperCase();
  const mB = s.match(/^B\s*(\d+)/);             // B2, B 2
  if (mB) return -parseInt(mB[1], 10);
  const mN = s.match(/^(\d+)\s*(F|층)?$/);       // 1, 1F, 1 층
  if (mN) return parseInt(mN[1], 10);
  if (/ROOF|옥상/.test(s)) return 999;
  return 0;                                      // 그 외는 0 근처
}

export default function FloorSelector() {
  const { locationName } = useParams(); // /locations/:locationName/floors
  const [floors, setFloors] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [deletingName, setDeletingName] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    setIsAdmin(getRoleFromToken() === 'ADMIN');
    fetchFloors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName]);

  const fetchFloors = async () => {
    try {
      const res = await api.get('/floors', { params: { location: locationName } });
      setFloors(Array.isArray(res.data) ? res.data : []);
      setErrorMsg('');
    } catch (err) {
      console.error('층 목록 로드 실패', err?.response || err);
      setErrorMsg('층 목록을 불러오지 못했습니다.');
    }
  };

  const handleClickFloor = (floor) => {
    navigate(`/locations/${encodeURIComponent(locationName)}/floors/${encodeURIComponent(floor.name)}`);
  };

  const handleModalDone = () => {
    setShowModal(false);
    fetchFloors(); // 추가 후 갱신
  };

  // --- 삭제 핸들러 ---
  const handleDeleteFloor = async (e, name) => {
    // 행 클릭으로의 네비게이션 방지
    e.stopPropagation();
    if (!isAdmin) return;

    const ok = window.confirm(`정말 "${name}" 층을 삭제하시겠습니까?`);
    if (!ok) return;

    setDeletingName(name);
    setErrorMsg('');

    // 낙관적 업데이트용 백업
    const prev = floors;
    setFloors((list) => list.filter((f) => f.name !== name));

    try {
      await api.delete('/floors', { params: { location: locationName, name } });
      // 성공 시: 이미 목록에서 제거됨
    } catch (err) {
      console.error('층 삭제 실패', err?.response || err);
      setErrorMsg('삭제에 실패했습니다. 새로고침 후 다시 시도하세요.');
      // 롤백
      setFloors(prev);
    } finally {
      setDeletingName(null);
    }
  };

  // 화면엔 name만 보여주되, 정렬은 자연스럽게
  const rows = useMemo(
    () => [...floors].sort((a, b) => floorOrder(a?.name) - floorOrder(b?.name)),
    [floors]
  );

  return (
    <div className="location-dashboard-container">
      <h1>{locationName} — 층 목록</h1>

      {isAdmin && (
        <div className="location-input-group" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" onClick={() => setShowModal(true)}>층 추가</button>
        </div>
      )}

      {showModal && (
        <FloorAddModal
          locationName={locationName}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalDone}
        />
      )}

      {errorMsg && (
        <div role="alert" style={{ color: 'crimson', margin: '8px 0' }}>
          {errorMsg}
        </div>
      )}

      <table className="location-table">
        <thead>
          <tr>
            <th>층 이름</th>
            {isAdmin && <th style={{ width: 120 }}>액션</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr
              key={f.id ?? f.name}
              onClick={() => handleClickFloor(f)}
              style={{ cursor: 'pointer' }}
              title={f.name}
            >
              <td>{f.name}</td>
              {isAdmin && (
                <td>
                  <button
                    type="button"
                    aria-label={`${f.name} 삭제`}
                    onClick={(e) => handleDeleteFloor(e, f.name)}
                    disabled={deletingName === f.name}
                    style={{
                      cursor: deletingName === f.name ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {deletingName === f.name ? '삭제 중…' : '삭제'}
                  </button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={isAdmin ? 2 : 1}>등록된 층이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
